import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import { Box, Stack, Skeleton, Typography, useTheme, useMediaQuery, Button, Toolbar, Paper } from '@mui/material';
import LinkOffRounded from '@mui/icons-material/LinkOffRounded';
import { formatDate } from '@/helpers/formatDate';
import useAlertStore from '@/stores/alertStore';
import { useAuthStore } from '@/stores/authStore';
import { resolveMediaUrl } from '@/utils/firebaseUtils';
import type { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import useFetchTranscript from '@/hooks/useFetchTranscript';
import VideoPlayer from '@/pages/Note/components/VideoPlayer/VideoPlayer';
import AudioPlayer from '@/pages/Note/components/AudioPlayer/AudioPlayer';
import VideoChapters from '@/pages/Note/components/VideoPlayer/VideoChapters';
import AudioChapters from '@/pages/Note/components/AudioPlayer/AudioChapters';
import Summary from '@/pages/Note/components/Summary/Summary';
import RightTabs from '@/pages/Note/components/RightTabs';
import { usePlayTracking } from '@/hooks/usePlayTracking';
import { Helmet } from 'react-helmet-async';
import { getInitialAssessmentFromContentAnalysis } from '@/utils/contentAnalysis';
import ContentAnalysis from '@/pages/Note/components/DocumentChapters/ContentAnalysis';
import { PublicNavbar } from '@/components/Navbar/PublicNavbar';
import PublicNoteHeader from './components/PublicNoteHeader';
import SignupCTA from './components/SignupCTA';
import { PublicNoteContainer } from './styles';
import { FIREBASE_STORAGE_ERRORS, truncateErrorMessage } from '@/helpers/errors';

const PublicNote = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const showAlert = useAlertStore((state) => state.showAlert);
  const { userLoggedIn, loading: authLoading } = useAuthStore();
  const [noteData, setNoteData] = useState<NoteOut | null>(null);
  const [isLoadingNote, setIsLoadingNote] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState('');
  const [signedThumbnailUrl, setSignedThumbnailUrl] = useState<string | undefined>();
  const [sharedBy, setSharedBy] = useState<{ name: string; email?: string } | null>(null);

  // Redirect logged-in users to the authenticated shared view
  // Both routes use the same /shared/{token} API - the difference is just the UI
  useEffect(() => {
    if (!authLoading && userLoggedIn && token) {
      navigate(`/shared/${token}`, { replace: true });
    }
  }, [authLoading, userLoggedIn, token, navigate]);

  useEffect(() => {
    if (!token) return;

    // Own the controller and timeout at effect scope so the cleanup below can
    // abort them. Created inside the async body they were unreachable, so
    // switching tokens left the previous request in flight — its response could
    // land after the new one and render note A's data under token B.
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 30000); // 30 second timeout
    let cancelled = false;

    const fetchPublicNote = async () => {
      setIsLoadingNote(true);
      setError(null);
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/shared/${token}`, {
          signal: controller.signal,
        });

        window.clearTimeout(timeoutId);

        if (!res.ok) {
          // Try to get the actual error message from the API response
          const errorData = await res.json().catch(() => null);
          const errorMessage = typeof errorData?.detail === 'string' ? errorData.detail : undefined;

          if (errorMessage) {
            throw new Error(errorMessage);
          } else if (res.status === 404) {
            throw new Error('This shared note no longer exists or the link is invalid.');
          } else if (res.status === 410) {
            throw new Error('This share link has expired. Ask the note owner to share it again.');
          } else {
            throw new Error('Failed to load note.');
          }
        }
        const data = await res.json();
        if (cancelled) return;

        const noteData = data.note || data;
        setNoteData(noteData);

        if (noteData.shared_by && noteData.shared_by.first_name) {
          setSharedBy({
            name: `${noteData.shared_by.first_name || ''} ${noteData.shared_by.last_name || ''}`.trim(),
            email: noteData.shared_by.email,
          });
        }
      } catch (err: unknown) {
        // A cancelled effect aborts its own request — that is not a user-facing error.
        if (cancelled) return;
        if (err instanceof Error) {
          if (err.name === 'AbortError') {
            setError('Request timed out. Please try again.');
          } else {
            setError(err.message);
          }
        } else {
          setError('An unexpected error occurred.');
        }
      } finally {
        if (!cancelled) setIsLoadingNote(false);
      }
    };

    fetchPublicNote();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [token]);

  const { isLoading: isLoadingTranscript, error: transcriptError } = useFetchTranscript(
    noteData?.transcript_url || null,
  );

  const {
    transcriptList: fullTranscriptList,
    isLoading: isLoadingFullTranscript,
    error: fullTranscriptError,
  } = useFetchTranscript(noteData?.full_transcript_url || null);

  const { trackPlay } = usePlayTracking({
    note: noteData,
    isShared: true,
    shareToken: token,
  });

  useEffect(() => {
    // Show only one transcript error alert to avoid duplicates
    if (transcriptError || fullTranscriptError) {
      showAlert(truncateErrorMessage(transcriptError || fullTranscriptError), 'error');
    }
  }, [transcriptError, fullTranscriptError, showAlert]);

  useEffect(() => {
    // Reset resolved URLs when noteData changes so SPA navigation between
    // tokens cannot leak the previous note's media or thumbnail (e.g. stale
    // og:image meta tags on a note that has no thumbnail_url).
    setSignedUrl('');
    setSignedThumbnailUrl(undefined);
    if (noteData) {
      const filePath = noteData.video_url || noteData.audio_url || '';
      if (filePath) {
        resolveMediaUrl(filePath)
          .then((url) => {
            if (url) setSignedUrl(url);
          })
          .catch((err: unknown) => {
            const firebaseError = err as { code?: string };
            const errorCode = firebaseError?.code || '';

            if (
              errorCode === FIREBASE_STORAGE_ERRORS.UNAUTHORIZED ||
              errorCode === FIREBASE_STORAGE_ERRORS.UNAUTHENTICATED
            ) {
              showAlert(
                'You do not have permission to view this shared content. The sharing permissions may have been changed.',
                'error',
              );
            } else if (errorCode === FIREBASE_STORAGE_ERRORS.OBJECT_NOT_FOUND) {
              showAlert('The media file could not be found. It may have been deleted or moved.', 'error');
            } else {
              showAlert('Error loading media.', 'error');
            }
          });
      }

      // Fetch thumbnail URL independently - silent failure (progressive enhancement)
      if (noteData.thumbnail_url) {
        resolveMediaUrl(noteData.thumbnail_url)
          .then((url) => {
            if (url) setSignedThumbnailUrl(url);
          })
          .catch(() => {
            // Silently ignore - older notes may not have thumbnails
          });
      }
    }
  }, [noteData, showAlert]);

  if (isLoadingNote) {
    return (
      <>
        <PublicNavbar />
        <Toolbar />
        <PublicNoteContainer>
          <Stack
            alignItems="center"
            maxWidth="1620px"
            width="100%"
            sx={{
              mx: 'auto',
              px: {
                xs: 2,
                sm: 4,
                md: 6,
                lg: 8,
              },
              pt: 2.5,
              pb: {
                xs: 2,
                sm: 4,
                md: 6,
              },
            }}
          >
            <Box sx={{ width: '100%', mb: 1 }}>
              <Skeleton variant="text" width="60%" height={36} />
              <Skeleton variant="text" width="25%" height={20} sx={{ mt: 0.5 }} />
            </Box>
            <Grid container spacing={{ xs: 0, sm: 2, md: 4 }} width="100%" sx={{ pt: 2 }}>
              <Grid
                size={{ xs: 12, sm: 12, md: 7.5 }}
                sx={{
                  '@media (max-width: 600px)': {
                    paddingRight: '0 !important',
                    paddingLeft: '0 !important',
                  },
                }}
              >
                <Box
                  sx={{
                    maxWidth: isMobile ? '100%' : '800px',
                    borderRadius: isMobile ? '12px' : '22px',
                    overflow: 'hidden',
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    sx={{
                      aspectRatio: '16 / 9',
                      height: 'auto',
                      maxHeight: isMobile ? '60vh' : '70vh',
                    }}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 12, md: 4.5 }} sx={{ mt: { xs: 3, sm: 3, md: 0 } }}>
                <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
              </Grid>
            </Grid>
          </Stack>
        </PublicNoteContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PublicNavbar />
        <Toolbar />
        <PublicNoteContainer>
          <Stack
            alignItems="center"
            justifyContent="center"
            minHeight="60vh"
            sx={{
              px: { xs: 2, sm: 4, md: 6, lg: 8 },
              py: { xs: 2, sm: 4, md: 6 },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                maxWidth: 480,
                width: '100%',
                textAlign: 'center',
                p: { xs: 3, sm: 4 },
                borderRadius: '23px',
                border: '1px solid',
                borderColor: 'neutral.otherBorder',
                boxShadow: '0px 4px 20px 0px rgba(129, 129, 129, 0.12)',
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: 'neutral.lightBackground',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2.5,
                }}
              >
                <LinkOffRounded sx={{ fontSize: 28, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'grey.900' }}>
                Hmm, we can't find this note
              </Typography>
              <Typography variant="body1" sx={{ color: 'grey.600', mb: 2 }}>
                The link may have expired or the note is no longer shared. Ask the person who shared it to send a fresh
                link.
              </Typography>
              <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mb: 3 }}>
                {truncateErrorMessage(error)}
              </Typography>
              <Button
                variant="contained"
                href="/login?action=signup"
                sx={{
                  px: 3,
                  py: 1,
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Go to Login
              </Button>
            </Paper>
          </Stack>
        </PublicNoteContainer>
      </>
    );
  }

  if (!noteData) return null;

  const isAudioOnly = Boolean(noteData.audio_url) && !noteData.video_url;
  const isProcessing = false;
  const noteTitle = noteData.title || 'Untitled Note';
  const noteSummary = noteData.summary || '';
  const initialAssessment = getInitialAssessmentFromContentAnalysis(noteData.content_analysis);

  return (
    <>
      <Helmet>
        <title>{noteTitle} - NoteGo</title>
        <meta name="description" content={noteSummary} />
        <meta property="og:title" content={noteTitle} />
        <meta property="og:description" content={noteSummary} />
        <meta property="og:type" content={isAudioOnly ? 'music.song' : 'video.other'} />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content={signedThumbnailUrl ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={noteTitle} />
        <meta name="twitter:description" content={noteSummary} />
        {signedThumbnailUrl && <meta property="og:image" content={signedThumbnailUrl} />}
        {signedThumbnailUrl && <meta name="twitter:image" content={signedThumbnailUrl} />}
      </Helmet>

      <PublicNavbar />
      <Toolbar />
      <PublicNoteContainer>
        <Stack
          alignItems="center"
          maxWidth="1620px"
          width="100%"
          sx={{
            mx: 'auto',
            px: {
              xs: 2, // 16px on mobile
              sm: 4, // 32px on tablet
              md: 6, // 48px on desktop
              lg: 8, // 64px on large screens
            },
            pt: 2.5, // 20px top padding
            pb: {
              xs: 2, // 16px bottom on mobile
              sm: 4, // 32px bottom on tablet
              md: 6, // 48px bottom on desktop
            },
          }}
        >
          <PublicNoteHeader
            title={noteTitle}
            date={formatDate(noteData.created_at || '')}
            isLoadingNote={false}
            sharedBy={sharedBy}
          />

          <Grid container spacing={{ xs: 0, sm: 2, md: 4 }} width="100%" sx={{ pt: 2 }}>
            {/* Main Content - Left Side */}
            <Grid
              size={{ xs: 12, sm: 12, md: 7.5 }}
              sx={{
                '@media (max-width: 600px)': {
                  paddingRight: '0 !important',
                  paddingLeft: '0 !important',
                },
              }}
            >
              {signedUrl ? (
                isAudioOnly ? (
                  <AudioPlayer src={signedUrl} chapters={noteData.document_chapter} />
                ) : (
                  <VideoPlayer
                    src={signedUrl}
                    chapters={noteData.document_chapter}
                    isMobile={isMobile}
                    onPlay={trackPlay}
                    posterUrl={signedThumbnailUrl}
                    videoWidth={noteData.metadata?.media?.webm?.width}
                    videoHeight={noteData.metadata?.media?.webm?.height}
                  />
                )
              ) : (
                <Box
                  sx={{
                    maxWidth: isMobile ? '100%' : '800px',
                    borderRadius: isMobile ? '12px' : '22px',
                    overflow: 'hidden',
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    sx={{
                      aspectRatio:
                        noteData.metadata?.media?.webm?.width && noteData.metadata?.media?.webm?.height
                          ? `${noteData.metadata.media.webm.width} / ${noteData.metadata.media.webm.height}`
                          : '16 / 9',
                      height: 'auto',
                      maxHeight: isMobile ? '60vh' : '70vh',
                    }}
                  />
                </Box>
              )}

              <Summary
                content={noteData.summary || ''}
                isOwner={false}
                isProcessing={isProcessing}
                isLoadingNote={false}
              />

              {/* Mobile CTA - After Summary */}
              {isMobile && <SignupCTA variant="inline" />}

              {noteData.document_chapter &&
                (isAudioOnly ? (
                  <AudioChapters chapters={noteData.document_chapter} isProcessing={false} />
                ) : (
                  <VideoChapters chapters={noteData.document_chapter} isProcessing={false} />
                ))}
              {initialAssessment && <ContentAnalysis initialAssessment={initialAssessment} />}
            </Grid>

            {/* Right Sidebar - Transcript & Tags */}
            <Grid
              size={{ xs: 12, sm: 12, md: 4.5 }}
              sx={{
                mt: { xs: 3, sm: 3, md: 0 },
                '@media (max-width: 600px)': {
                  paddingLeft: '0 !important',
                },
              }}
            >
              {/* Desktop/Tablet Floating CTA Card */}
              {!isMobile && <SignupCTA variant="card" />}

              <RightTabs
                tags={noteData.tags || []}
                isAudioOnly={isAudioOnly}
                isOwner={false}
                isProcessing={isProcessing}
                isLoading={isLoadingTranscript || isLoadingFullTranscript}
                fullTranscriptList={fullTranscriptList}
                keyMoments={noteData.key_moments || null}
                isPublicView
              />
            </Grid>
          </Grid>
        </Stack>
      </PublicNoteContainer>
    </>
  );
};

export default PublicNote;
